export const MEMBERSHIP_SELECT_CLASS =
  'w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm';

export const userLabel = (item) => {
  const name = `${item?.firstName || ''} ${item?.lastName || ''}`.trim();
  return name || item?.username || 'User';
};

const treeDepthOfRole = (role, orgRoles = []) => {
  if (!role) return 10000;
  const seen = new Set();
  let depth = 0;
  let cursor = role.reportsToRoleId || '';
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    depth += 1;
    const parent = (orgRoles || []).find((item) => item.id === cursor);
    cursor = parent?.reportsToRoleId || '';
  }
  return depth;
};

export const rankOfUser = (item, orgRoles = []) => {
  if (!item) return 10000;
  if (item.role === 'ADMIN') return -1;
  if (item.role === 'ORG_ADMIN') return 0;
  if (item.organizationRoleId) {
    return rankOfRoleSelection(item.organizationRoleId, orgRoles);
  }
  if (typeof item.roleRank === 'number' && item.roleRank >= 0) return item.roleRank;
  if (item.role === 'MANAGER') return 2;
  return 10000;
};

export const rankOfRoleSelection = (roleId, orgRoles = []) => {
  if (!roleId) return 10000;
  if (roleId === '__org_admin__') return 0;
  const found = (orgRoles || []).find((role) => role.id === roleId);
  return found ? treeDepthOfRole(found, orgRoles) + 1 : 10000;
};

export const higherRoleIds = (roleId, orgRoles = []) => {
  if (!roleId || roleId === '__org_admin__') return new Set();
  const selectedRank = rankOfRoleSelection(roleId, orgRoles);
  const ids = new Set();
  (orgRoles || []).forEach((role) => {
    if (rankOfRoleSelection(role.id, orgRoles) < selectedRank) {
      ids.add(role.id);
    }
  });
  return ids;
};

export const supervisorCandidates = (people, { excludeUserId, targetRank, orgRoles = [], higherIds } = {}) =>
  (people || []).filter((item) => {
    if (!item?.id || item.id === excludeUserId) return false;
    if (item.role === 'ADMIN') return false;
    if (item.verified === false) return false;
    if (item.role === 'ORG_ADMIN') return targetRank > 0;
    if (higherIds && item.organizationRoleId) {
      return higherIds.has(item.organizationRoleId);
    }
    return rankOfUser(item, orgRoles) < targetRank;
  });

export const supervisorGroups = (people, { excludeUserId, roleId, orgRoles = [] } = {}) => {
  const targetRank = rankOfRoleSelection(roleId, orgRoles);
  const higherIds = higherRoleIds(roleId, orgRoles);
  const candidates = supervisorCandidates(people, {
    excludeUserId,
    targetRank,
    orgRoles,
    higherIds,
  });
  const groups = [];
  const orgAdmins = candidates.filter((item) => item.role === 'ORG_ADMIN');
  if (orgAdmins.length) {
    groups.push({ key: '__org_admin__', name: 'Org admin', people: orgAdmins });
  }
  [...orgRoles]
    .sort((a, b) => rankOfRoleSelection(a.id, orgRoles) - rankOfRoleSelection(b.id, orgRoles)
      || String(a.name || '').localeCompare(String(b.name || '')))
    .forEach((role) => {
      if (!higherIds.has(role.id)) return;
      const members = candidates.filter((item) => item.organizationRoleId === role.id);
      if (members.length) {
        groups.push({ key: role.id, name: role.name, people: members });
      }
    });
  const groupedIds = new Set(groups.flatMap((group) => group.people.map((item) => item.id)));
  const leftover = candidates.filter((item) => !groupedIds.has(item.id));
  if (leftover.length) {
    groups.push({ key: '__higher__', name: 'Higher role', people: leftover });
  }
  return groups;
};
